import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/** Responsable proponible, tal como lo devuelve el registro legal (RF-005). */
export interface Responsable {
  id: string;
  /** REPRESENTANTE_LEGAL | USUARIO_AUTORIZADO — cadena, no unión cerrada, para
   *  que un tercer tipo no obligue a tocar este componente. */
  tipo: string;
  nombreCompleto: string;
}

const ETIQUETA_TIPO: Record<string, string> = {
  REPRESENTANTE_LEGAL: "Representante legal",
  USUARIO_AUTORIZADO: "Usuario autorizado",
};

/** Mensaje textual del RN-005; se muestra tal cual lo pide el requerimiento. */
export const MENSAJE_CAPTURA_MANUAL =
  "No fue posible recuperar responsables registrados. Capture manualmente la información correspondiente.";

/**
 * Selector de responsable alimentado por el registro legal vigente del titular
 * (RF-005).
 *
 * Comportamiento:
 *  - Con responsables registrados: solo se puede elegir de la lista; si hay
 *    exactamente uno queda seleccionado automáticamente (RN-002).
 *  - Sin registro legal, sin responsables o si la consulta falla: se habilita
 *    la captura libre con el mensaje del RN-005. La captura manual no está
 *    visible de forma predeterminada (RN-006).
 *
 * El valor que emite es el **nombre**, no el id, porque la solicitud guarda al
 * responsable como texto: es la foto de quién firmó en ese momento y no debe
 * cambiar si después se revoca a esa persona en el registro legal.
 */
export function ResponsablePicker({
  label,
  registroLegalId,
  value,
  onChange,
  required,
  disabled,
  tipos,
}: {
  label: string;
  /** Registro legal del titular. Sin él se cae al escenario de excepción. */
  registroLegalId: string | null | undefined;
  value: string;
  onChange: (nombre: string) => void;
  required?: boolean;
  disabled?: boolean;
  /** Filtra por tipo de responsable. Sin especificar, se ofrecen todos. */
  tipos?: string[];
}) {
  const q = useQuery<Responsable[]>({
    queryKey: ["responsables", registroLegalId],
    queryFn: async () =>
      (await api.get<Responsable[]>(`/api/registros-legales/${registroLegalId}/responsables`)).data,
    enabled: !!registroLegalId,
    // Un 403 o un 404 no son un error a reintentar: son el escenario de
    // excepción del RN-005 y deben resolverse habilitando la captura manual.
    retry: false,
  });

  const opciones = useMemo(() => {
    const lista = q.data ?? [];
    return tipos?.length ? lista.filter((r) => tipos.includes(r.tipo)) : lista;
  }, [q.data, tipos]);

  // Sin titular elegido todavía no hay a quién consultar: no es el escenario de
  // excepción del RN-005 y no debe anunciarse como una falla.
  const sinTitular = !registroLegalId;
  const cargando = !!registroLegalId && q.isLoading;
  // Solo se considera «hay información» cuando la consulta terminó bien y trajo
  // a alguien; cualquier otro desenlace cae en captura manual.
  const hayRegistrados = q.isSuccess && opciones.length > 0;

  // RN-002: con un único responsable, se propone sin intervención del usuario.
  useEffect(() => {
    if (hayRegistrados && opciones.length === 1 && !value) {
      onChange(opciones[0].nombreCompleto);
    }
    // Si el valor elegido dejó de estar en la lista (cambió el titular), se
    // limpia para no enviar a alguien que no pertenece a esta empresa.
    if (hayRegistrados && value && !opciones.some((o) => o.nombreCompleto === value)) {
      onChange("");
    }
  }, [hayRegistrados, opciones, value, onChange]);

  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-fg-subtle">
        {label}
        {required && <span className="text-estado-err"> *</span>}
      </label>

      {sinTitular ? (
        <div className="flex h-10 items-center rounded-md border border-edge bg-surface-2 px-3 text-sm text-fg-subtle">
          Selecciona primero la empresa titular.
        </div>
      ) : cargando ? (
        <div className="flex h-10 items-center gap-2 rounded-md border border-edge bg-surface-2 px-3 text-sm text-fg-subtle">
          <Loader2 className="h-4 w-4 animate-spin" /> Consultando el registro legal…
        </div>
      ) : hayRegistrados ? (
        <select
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm text-fg shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-surface-2"
        >
          <option value="">— Selecciona —</option>
          {opciones.map((r) => (
            <option key={r.id} value={r.nombreCompleto}>
              {r.nombreCompleto}
              {ETIQUETA_TIPO[r.tipo] ? ` — ${ETIQUETA_TIPO[r.tipo]}` : ""}
            </option>
          ))}
        </select>
      ) : (
        <>
          <input
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-surface-2"
          />
          <p className="mt-1 flex items-start gap-1 text-xs text-estado-warn">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
            {MENSAJE_CAPTURA_MANUAL}
          </p>
        </>
      )}

      {required && !value && !cargando && !sinTitular && (
        <p className="mt-1 text-xs text-estado-warn">Obligatorio</p>
      )}
    </div>
  );
}
